import { IconButton, MenuItem, MenuList, Stack, Typography } from '@mui/material';
import { AnalyticsEventName, trackAmplitudeEvent } from '@schooly/analytics';
import {
  DATE_FORMAT_RANGE_FNS,
  useGetAttendanceCodesQuery,
  useGetAttendanceRegisterStatisticsQuery,
  useGetAttendanceStatisticsQuery,
} from '@schooly/api';
import { useAuth } from '@schooly/components/authentication';
import { CrossIcon, EditIcon, Loading, ModalHeader } from '@schooly/style';
import { newDateTimezoneOffset } from '@schooly/utils/date';
import { format } from 'date-fns';
import React, { FC, useEffect, useMemo, useRef, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import { useNavigate, useParams } from 'react-router-dom';

import { AccessDenied } from '@schooly/style';
import { CollapsiblePanel } from '../../../components/common/CollapsiblePanel';
import {
  ModalLarge,
  ModalMain,
  ModalSideBar,
} from '../../../components/uikit-components/Modal/Modal.styled';
import { ModalHeaderV2 } from '../../../components/uikit-components/Modal/ModalHeaderV2';
import { useRouter } from '../../../context/router/useRouter';
import useSchoolYears from '../../../hooks/useSchoolYears';
import { AttendanceRegisterPreviewPeriodsTab } from './AttendanceRegisterPreviewPeriodsTab';
import { AttendanceRegisterPreviewSessionsTab } from './AttendanceRegisterPreviewSessionsTab';
import { canEditRegister, isRegisterDateEditable } from '../permissions';

interface AttendanceRegisterPreviewModalProps {}

enum RegisterPreviewTab {
  Sessions = 'sessions',
  Periods = 'periods',
}

export const AttendanceRegisterPreviewModal: FC<AttendanceRegisterPreviewModalProps> = () => {
  const { schoolId = '', permissions, currentUser } = useAuth();
  const hasTrackedOpen = useRef(false);
  const { data: codes } = useGetAttendanceCodesQuery(schoolId);
  const navigate = useNavigate();
  const { closeAndClean: closeModal } = useRouter();
  const { id } = useParams();
  const { defaultValidity, isLoading: isLoadingSchoolYears } = useSchoolYears();
  const [tab, setTab] = useState(RegisterPreviewTab.Sessions);

  useEffect(() => {
    if (hasTrackedOpen.current) return;

    const userId = currentUser?.user_id;
    if (!userId) return;

    hasTrackedOpen.current = true;
    trackAmplitudeEvent({
      name: AnalyticsEventName.AttendanceRegisterOpen,
      payload: { user_id: userId, user_roles: permissions },
    });
  }, [currentUser, permissions]);

  const {
    data: register,
    isLoading: isLoadingRegister,
    error: registerError,
  } = useGetAttendanceRegisterStatisticsQuery(id ?? '', schoolId, {
    enabled: Boolean(id && schoolId),
    refetchOnMount: 'always',
  });

  const enabledYearStatsQuery = Boolean(schoolId && defaultValidity);
  const { data: yearStats, isLoading: isLoadingYearStats } = useGetAttendanceStatisticsQuery(
    {
      schoolId,
      dateFrom: defaultValidity?.start ?? '',
      dateTo: defaultValidity?.end ?? '',
    },
    { enabled: enabledYearStatsQuery },
  );

  const registerHasPeriodRecords = useMemo(
    () =>
      register?.totals.some(
        ({ period_ids, attendance_details }) =>
          period_ids.length > 0 && attendance_details.some(({ count }) => count > 0),
      ) ?? false,
    [register],
  );

  const showPeriodsTab = registerHasPeriodRecords || (yearStats?.period_entries_count ?? 0) > 0;

  const isYearStatsLoading = isLoadingSchoolYears || (enabledYearStatsQuery && isLoadingYearStats);
  const isLoading = isLoadingRegister || (!registerHasPeriodRecords && isYearStatsLoading);
  const showLoading = !registerError && isLoading;

  const canEdit =
    canEditRegister(permissions) &&
    !!register &&
    !!defaultValidity &&
    isRegisterDateEditable(register.date, defaultValidity);

  const renderContent = () => {
    if (showLoading) return <Loading />;

    if (registerError || !register) {
      return (
        <>
          <ModalHeader withBorderBottom={false} active>
            <IconButton onClick={closeModal}>
              <CrossIcon />
            </IconButton>
          </ModalHeader>
          <AccessDenied />
        </>
      );
    }

    return (
      <>
        <ModalHeaderV2
          withGoBack
          active
          title={
            <Typography variant="h1">
              {format(newDateTimezoneOffset(register.date), DATE_FORMAT_RANGE_FNS)}
            </Typography>
          }
        >
          {canEdit && (
            <IconButton onClick={() => navigate(`/attendance/${register.id}/edit`)}>
              <EditIcon />
            </IconButton>
          )}

          <IconButton onClick={closeModal}>
            <CrossIcon />
          </IconButton>
        </ModalHeaderV2>
        <ModalMain>
          <CollapsiblePanel>
            <ModalSideBar
              sx={{
                paddingTop: (theme) => theme.spacing(1),
              }}
            >
              <MenuList>
                <MenuItem
                  selected={tab === RegisterPreviewTab.Sessions}
                  onClick={() => setTab(RegisterPreviewTab.Sessions)}
                >
                  <FormattedMessage id="attendance-AmPmAttendance" />
                </MenuItem>
                {showPeriodsTab && (
                  <MenuItem
                    selected={tab === RegisterPreviewTab.Periods}
                    onClick={() => setTab(RegisterPreviewTab.Periods)}
                  >
                    <FormattedMessage id="attendance-Frequencies-PerPeriod" />
                  </MenuItem>
                )}
              </MenuList>
            </ModalSideBar>
          </CollapsiblePanel>

          <Stack p={3} width="100%" gap={1} overflow="hidden">
            {tab === RegisterPreviewTab.Sessions ? (
              <AttendanceRegisterPreviewSessionsTab register={register} codes={codes ?? []} />
            ) : (
              <AttendanceRegisterPreviewPeriodsTab register={register} codes={codes ?? []} />
            )}
          </Stack>
        </ModalMain>
      </>
    );
  };

  return (
    <ModalLarge open onClose={closeModal}>
      {renderContent()}
    </ModalLarge>
  );
};
